/**
 * ObstructionContractor
 *
 * "Contractor" section of the obstruction details panel — read-only display of
 * the embedded contractor block. Company / manager / phone are always present
 * (mandatory per backend domain rules); the remaining fields are rendered only
 * when set. Phone fields expose a `tel:` link, email a `mailto:` link.
 *
 * Editing contractor data happens exclusively through the obstruction edit form
 * (Step 2) — consistent with the rest of the detail panel, where the info grid
 * is display-only.
 */
import { memo } from 'react';
import { Anchor, Box, Group, Input, Stack, Text } from '@mantine/core';
import {
  IconBuilding,
  IconCalendarDue,
  IconCalendarEvent,
  IconFileCertificate,
  IconMail,
  IconPhone,
  IconPhoneCall,
  IconUser,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { useLocalizedDate } from '@/hooks/useLocalizedDate';
import { ICON_SIZE_TAB } from '@/constants/ui';
import { formatPhoneNumber } from '@/utils';
import type { Obstruction } from '@/types/obstructions';

import styles from './ObstructionContractor.module.css';

interface ObstructionContractorProps {
  obstruction: Obstruction;
}

export const ObstructionContractor = memo(function ObstructionContractor({
  obstruction,
}: ObstructionContractorProps) {
  const { t } = useTranslation();
  const { formatDateCompact } = useLocalizedDate();

  const { contractor } = obstruction;

  return (
    <Stack gap="lg">
      <Box className={styles.infoGrid}>
        {/* Company (required) */}
        <Box className={styles.infoItem}>
          <Group gap="xs" align="center">
            <IconBuilding size={ICON_SIZE_TAB} />
            <Input.Label>
              {t('obstructions.contractor.fields.company')}
            </Input.Label>
          </Group>
          <Text size="sm" className={styles.infoValue}>
            {contractor.company}
          </Text>
        </Box>

        {/* Manager (required) */}
        <Box className={styles.infoItem}>
          <Group gap="xs" align="center">
            <IconUser size={ICON_SIZE_TAB} />
            <Input.Label>
              {t('obstructions.contractor.fields.manager')}
            </Input.Label>
          </Group>
          <Text size="sm" className={styles.infoValue}>
            {contractor.manager}
          </Text>
        </Box>

        {/* Phone (required) — tel: link */}
        <Box className={styles.infoItem}>
          <Group gap="xs" align="center">
            <IconPhone size={ICON_SIZE_TAB} />
            <Input.Label>
              {t('obstructions.contractor.fields.phone')}
            </Input.Label>
          </Group>
          <Anchor
            href={`tel:${encodeURIComponent(contractor.phone)}`}
            size="sm"
            className={styles.linkValue}
          >
            {formatPhoneNumber(contractor.phone)}
          </Anchor>
        </Box>

        {/* Email (optional) — mailto: link */}
        {contractor.email !== null && contractor.email.length > 0 && (
          <Box className={styles.infoItem}>
            <Group gap="xs" align="center">
              <IconMail size={ICON_SIZE_TAB} />
              <Input.Label>
                {t('obstructions.contractor.fields.email')}
              </Input.Label>
            </Group>
            <Anchor
              href={`mailto:${encodeURIComponent(contractor.email)}`}
              size="sm"
              className={styles.linkValue}
            >
              {contractor.email}
            </Anchor>
          </Box>
        )}

        {/* Emergency contact (optional) — tel: link */}
        {contractor.emergencyContact !== null &&
          contractor.emergencyContact.length > 0 && (
            <Box className={styles.infoItem}>
              <Group gap="xs" align="center">
                <IconPhoneCall size={ICON_SIZE_TAB} />
                <Input.Label>
                  {t('obstructions.contractor.fields.emergencyContact')}
                </Input.Label>
              </Group>
              <Anchor
                href={`tel:${encodeURIComponent(contractor.emergencyContact)}`}
                size="sm"
                className={styles.linkValue}
              >
                {formatPhoneNumber(contractor.emergencyContact)}
              </Anchor>
            </Box>
          )}

        {/* Permit number (optional) */}
        {contractor.permitNumber !== null &&
          contractor.permitNumber.length > 0 && (
            <Box className={styles.infoItem}>
              <Group gap="xs" align="center">
                <IconFileCertificate size={ICON_SIZE_TAB} />
                <Input.Label>
                  {t('obstructions.contractor.fields.permitNumber')}
                </Input.Label>
              </Group>
              <Text size="sm" className={styles.infoValue}>
                {contractor.permitNumber}
              </Text>
            </Box>
          )}

        {/* Permit issued date (optional) */}
        {contractor.permitIssuedDate !== null && (
          <Box className={styles.infoItem}>
            <Group gap="xs" align="center">
              <IconCalendarEvent size={ICON_SIZE_TAB} />
              <Input.Label>
                {t('obstructions.contractor.fields.permitIssuedDate')}
              </Input.Label>
            </Group>
            <Text size="sm" className={styles.infoValue}>
              {formatDateCompact(contractor.permitIssuedDate)}
            </Text>
          </Box>
        )}

        {/* Permit expiry date (optional) */}
        {contractor.permitExpiryDate !== null && (
          <Box className={styles.infoItem}>
            <Group gap="xs" align="center">
              <IconCalendarDue size={ICON_SIZE_TAB} />
              <Input.Label>
                {t('obstructions.contractor.fields.permitExpiryDate')}
              </Input.Label>
            </Group>
            <Text size="sm" className={styles.infoValue}>
              {formatDateCompact(contractor.permitExpiryDate)}
            </Text>
          </Box>
        )}
      </Box>

      {/* Notes (optional, multiline) */}
      {contractor.notes !== null && contractor.notes.length > 0 && (
        <Box className={styles.notesItem}>
          <Input.Label>{t('obstructions.contractor.fields.notes')}</Input.Label>
          <Text size="sm" className={styles.notesValue}>
            {contractor.notes}
          </Text>
        </Box>
      )}
    </Stack>
  );
});
